import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Check, Save, Package } from 'lucide-react';
import { useUIStore } from '@/stores';
import { Service, ServiceCategory as Category, Unit } from '@/shared/model';
import { ResponsiveModal } from '@/shared/ui/responsive-modal';
import { Button } from '@/shared/ui/base';
import { Input } from '@/shared/ui/input';

interface ServiceSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  services: Service[];
  categories: Category[];
  units: Unit[];
  onSelect: (service: Service & { saveToDb?: boolean }) => void;
  title?: string;
}

export const ServiceSelectorModal: FC<ServiceSelectorModalProps> = ({
    isOpen,
    onClose,
    services,
    categories,
    units,
    onSelect,
    title
}) => {
  const { t } = useTranslation();
  const currency = useUIStore((s) => s.currency);
  const [tab, setTab] = useState<'list' | 'create'>('list');
  const [search, setSearch] = useState('');
  const [addedFeedback, setAddedFeedback] = useState<Record<string, boolean>>({});
  
  const [customForm, setCustomForm] = useState({
    name: '',
    price: '',
    categoryId: categories[0]?.id || 'cat_other',
    saveToDb: false
  });
  
  const [selectedUnitId, setSelectedUnitId] = useState('pcs');

  // Группировка услуг по категориям с фильтрацией по поиску
  const filteredGroupedServices = services.reduce((acc: Record<string, Service[]>, service) => {
    const translatedServiceName = service.nameKey ? t(service.nameKey, service.name) : service.name;
    if (!search || translatedServiceName.toLowerCase().includes(search.toLowerCase()) || service.name.toLowerCase().includes(search.toLowerCase())) {
      const categoryObj = categories.find(c => c.id === service.categoryId);
      const translatedCatName = categoryObj?.nameKey ? t(categoryObj.nameKey, categoryObj.name) : (categoryObj?.name || t('services.no_category', 'Без категории'));
      
      if (!acc[translatedCatName]) acc[translatedCatName] = [];
      acc[translatedCatName].push(service);
    }
    return acc;
  }, {});

  const handleAddCustom = () => {
    const newService: Service & { saveToDb: boolean } = {
      id: 'custom_' + Date.now(),
      name: customForm.name,
      categoryId: customForm.categoryId,
      unit: selectedUnitId,
      unitType: selectedUnitId,
      priceLabor: parseFloat(customForm.price) || 0,
      isCustom: true,
      saveToDb: customForm.saveToDb
    };
    
    onSelect(newService);
    setCustomForm({ name: '', price: '', categoryId: categories[0]?.id || 'cat_other', saveToDb: false });
    setSelectedUnitId('pcs');
    onClose();
  };

  const handleAddService = (service: Service) => {
    setAddedFeedback(prev => ({ ...prev, [service.id]: true }));
    onSelect(service);
    setTimeout(() => {
        setAddedFeedback(prev => {
            const next = { ...prev };
            delete next[service.id];
            return next;
        });
    }, 1000);
  };

  return (
    <ResponsiveModal 
        open={isOpen} 
        onOpenChange={(open) => !open && onClose()}
        title={title || t('services.select_title', 'Выбрать услугу')}
        className="max-w-2xl p-0 overflow-hidden"
        stickyHeader
    >
        <div className="flex flex-col h-full max-h-[80vh]">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex gap-2 shrink-0">
                <button 
                    onClick={() => setTab('list')}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                    tab === 'list' ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm ring-1 ring-slate-200 dark:ring-slate-700' : 'text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800'
                    }`}
                >
                    <Package size={16}/> {t('services.tab_list', 'Выбрать из базы')}
                </button>
                <button 
                    onClick={() => setTab('create')}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                    tab === 'create' ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm ring-1 ring-slate-200 dark:ring-slate-700' : 'text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800'
                    }`}
                >
                    <Plus size={16}/> {t('services.tab_create', 'Создать свою')}
                </button>
            </div>

            {tab === 'list' ? (
            <div className="flex flex-col flex-grow overflow-hidden bg-white dark:bg-slate-900">
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 sticky top-0 z-10 bg-white dark:bg-slate-900">
                    <div className="relative">
                        <Search className="absolute left-3 top-3 text-slate-400 dark:text-slate-500" size={18}/>
                        <Input 
                            className="pl-10"
                            placeholder={t('services.search_placeholder', 'Поиск по названию...')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
                <div className="overflow-y-auto px-4 pb-4 flex-grow bg-slate-50/50 dark:bg-slate-950">
                {Object.entries(filteredGroupedServices).map(([category, catServices]) => (
                    <div key={category} className="relative pt-4 bg-transparent">
                    <h4 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider sticky top-0 bg-slate-50/90 dark:bg-slate-950/90 backdrop-blur-md z-20 py-2 mb-2">
                        {category}
                    </h4>
                    <div className="grid grid-cols-1 gap-2 pb-4">
                        {catServices.map(service => {
                        const isJustAdded = addedFeedback[service.id];
                        const unitObj = units.find(u => u.id === (service.unitType || service.unit));
                        const unitName = unitObj?.nameKey ? t(unitObj.nameKey, unitObj.name) : (unitObj?.name || service.unit);
                        
                        return (
                            <button 
                            key={service.id}
                            type="button"
                            onClick={() => handleAddService(service)}
                            className={`flex items-center justify-between p-3 rounded-2xl border transition-all text-left group bg-white dark:bg-slate-900 ${
                                isJustAdded 
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600 shadow-sm' 
                                    : 'border-slate-200 dark:border-slate-800 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:shadow-md'
                            }`}
                            >
                            <div className="flex-1 pr-4">
                                <div className={`font-bold text-sm leading-tight transition-colors ${isJustAdded ? 'text-green-800 dark:text-green-300' : 'text-slate-700 dark:text-slate-200 group-hover:text-blue-700 dark:group-hover:text-blue-400'}`}>
                                    {service.nameKey ? t(service.nameKey, service.name) : service.name}
                                </div>
                                <div className={`text-xs mt-1 transition-colors ${isJustAdded ? 'text-green-600 dark:text-green-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                    <span className="font-mono tabular-nums">{service.priceLabor}</span> <span className="font-medium text-[10px] uppercase font-mono tabular-nums">{currency}</span> / {unitName}
                                </div>
                            </div>
                            
                            <div className={`
                                w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-300 flex-shrink-0
                                ${isJustAdded 
                                    ? 'bg-green-500 text-white rotate-0' 
                                    : 'bg-slate-50 dark:bg-slate-800 text-slate-300 dark:text-slate-600 group-hover:bg-blue-600 dark:group-hover:bg-blue-500 group-hover:text-white group-active:scale-90'}
                            `}>
                                {isJustAdded ? (
                                <Check size={20} className="stroke-[3px]" />
                                ) : (
                                <Plus size={20} className="stroke-[3px]" />
                                )}
                            </div>
                            </button>
                        );
                        })}
                    </div>
                    </div>
                ))}
                {Object.keys(filteredGroupedServices).length === 0 && (
                    <div className="text-center text-slate-400 dark:text-slate-600 py-10">{t('common.nothing_found', 'Ничего не найдено')}</div>
                )}
                </div>
            </div>
            ) : (
            <div className="p-6 flex flex-col gap-5 overflow-y-auto bg-white dark:bg-slate-900">
                <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase ml-1">{t('services.create_form.name_label', 'Название услуги')}</label>
                    <Input 
                        placeholder={t('services.create_form.name_placeholder', 'Например: Установка люстры')}
                        value={customForm.name}
                        onChange={(e) => setCustomForm({...customForm, name: e.target.value})}
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase ml-1">{t('services.create_form.category_label', 'Категория')}</label>
                        <select 
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 focus:border-blue-500 outline-none bg-slate-50 dark:bg-slate-800 font-bold text-sm dark:text-slate-100 transition-all"
                            value={customForm.categoryId}
                            onChange={(e) => setCustomForm({...customForm, categoryId: e.target.value})}
                        >
                        {categories.map(c => (
                            <option key={c.id} value={c.id} className="dark:bg-slate-900">
                                {c.nameKey ? t(c.nameKey, c.name) : c.name}
                            </option>
                        ))}
                        </select>
                    </div>
                    
                    <div className="space-y-1.5">
                        <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase ml-1">{t('services.create_form.unit_label', 'Ед. измерения')}</label>
                        <select 
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 focus:border-blue-500 outline-none bg-slate-50 dark:bg-slate-800 font-bold text-sm dark:text-slate-100 transition-all"
                            value={selectedUnitId}
                            onChange={(e) => setSelectedUnitId(e.target.value)}
                        >
                        {units.map(u => (
                            <option key={u.id} value={u.id} className="dark:bg-slate-900">
                                {u.nameKey ? t(u.nameKey, u.name) : u.name}
                            </option>
                        ))}
                        </select>
                    </div>
                </div>

                <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-slate-400 dark:text-slate-500 uppercase ml-1">{t('services.create_form.price_label', 'Цена работы ({{currency}})', { currency })}</label>
                    <Input 
                        type="number"
                        className="font-bold text-lg"
                        placeholder="0"
                        value={customForm.price}
                        onChange={(e) => setCustomForm({...customForm, price: e.target.value})}
                    />
                </div>

                <label className="flex items-center gap-3 p-4 border border-slate-200 dark:border-slate-700 rounded-2xl cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <input 
                        type="checkbox"
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                        checked={customForm.saveToDb}
                        onChange={(e) => setCustomForm({...customForm, saveToDb: e.target.checked})}
                    />
                    <div className="flex-1">
                        <div className="font-bold text-slate-700 dark:text-slate-200 text-sm">{t('services.create_form.save_db', 'Сохранить в базу')}</div>
                    </div>
                    <Save size={18} className={customForm.saveToDb ? "text-blue-600 dark:text-blue-400" : "text-slate-300 dark:text-slate-600"}/>
                </label>

                <Button 
                    size="lg"
                    onClick={handleAddCustom}
                    disabled={!customForm.name || !customForm.price}
                    className="mt-2"
                >
                    <Plus size={20}/> {t('services.create_form.submit_btn', 'Добавить услугу')}
                </Button>
            </div>
            )}
        </div>
    </ResponsiveModal>
  );
};
